import { LinkedList, ListNode } from './linkedlist';
/**
 * An LRU cache implementation backed by a LinkedList.
 */
export class LRUCache {
    /**
     * @param limit - The maximum number of items that can be stored in the cache.
     */
    constructor(limit) {
        /**
         * The maximum amount of items that can be stored in the cache.
         */
        this._limit = 100;
        /**
         * The underlying `LinkedList` instance that will be used to store the nodes.
         */
        this._list = new LinkedList();
        /**
         * A key-value map that is used to associate `ListNode`s to their key.
         */
        this._map = {};
        this._limit = limit;
    }
    /**
     * Returns the value for a corresponding key.
     *
     * @param key - The key of the item to retrieve from the cache.
     * @return The value of the key if it exists, or null otherwise.
     */
    get(key) {
        const node = this._map[key];
        if (!node) {
            return null;
        }
        const list = this._list;
        list.remove(node);
        list.append(node);
        return node.value;
    }
    /**
     * Stores the provided value in the cache under a particular key.
     *
     * @param key - The key for this cache value.
     * @param value - The value to store.
     */
    set(key, value) {
        if (!key) {
            throw new TypeError('Cache key cannot be empty.');
        }
        const list = this._list;
        const map = this._map;
        if (list.length >= this._limit) {
            const first = list.first;
            map[first.key] = null;
            list.remove(first);
        }
        let node = map[key];
        if (node) {
            list.remove(node);
            node.value = value;
        }
        else {
            node = new ListNode(value);
            node.key = key;
        }
        list.append(node);
        map[key] = node;
    }
    /**
     * Removes a value from the cache.
     *
     * @param key - The key for this cache value.
     * @return The value for the key if it exists, null otherwise.
     */
    remove(key) {
        const map = this._map;
        const node = map[key];
        if (!node) {
            return null;
        }
        this._list.remove(node);
        map[key] = null;
        return node.value;
    }
    /**
     * Returns an array of keys within the cache.
     *
     * @return An array of keys within the cache, ordered by most frequently
     *     accessed.
     */
    keys() {
        const keys = [];
        let current = this._list.first;
        while (current) {
            keys.push(current.key);
            current = current.next;
        }
        return keys.reverse();
    }
    /**
     * Returns an array of values within the cache.
     *
     * @return An array of values within the cache, ordered by most frequently
     *     accessed.
     */
    values() {
        const values = [];
        let current = this._list.first;
        while (current) {
            values.push(current.value);
            current = current.next;
        }
        return values.reverse();
    }
    /**
     * Returns the number of items within the cache.
     *
     * @return The number of items inside the cache.
     */
    size() {
        return this._list.length;
    }
    /**
     * Removes all items from within the cache.
     *
     * @return The number of items removed from the cache.
     */
    clear() {
        const length = this._list.length;
        this._list = new LinkedList();
        this._map = {};
        return length;
    }
}
//# sourceMappingURL=lrucache.js.map